/**
 * Uma mensagem.
 *
 * Mensagens agrupadas (mesmo autor, poucos minutos de diferença) escondem o
 * avatar e o nome e mostram o horário só ao passar o mouse. É o que mantém uma
 * conversa rápida legível em vez de virar uma coluna de cabeçalhos repetidos.
 */

import { memo, useContext, useState } from 'react'
import {
  Copy,
  CornerUpLeft,
  MoreHorizontal,
  Pencil,
  Pin,
  SmilePlus,
  Trash2,
} from 'lucide-react'
import { formatTime, type Message } from '@nexus/shared'
import { cn } from '@/lib/cn'
import { api } from '@/lib/api'
import { EmojiPicker } from '@/components/chat/EmojiPicker'
import { useAppStore } from '@/store/app'
import { useAuthStore } from '@/store/auth'
import { usePreferencesStore } from '@/store/preferences'
import { useMember } from '@/hooks/selectors'
import { Avatar, ContextMenu, MenuItem, MenuSeparator, Tooltip } from '@/components/ui'
import { MessageContent } from '@/components/chat/MessageContent'
import { AttachmentPreview } from '@/components/chat/AttachmentPreview'
import { MessageComposerContext } from '@/components/chat/composer-context'

const QUICK_REACTIONS = ['👍', '❤️', '😂', '🎉', '👀']

export const MessageItem = memo(function MessageItem({
  message,
  grouped,
  /** Piscando por ter acabado de ser alcançada pela lista de fixadas. */
  destacada,
}: {
  message: Message
  grouped: boolean
  destacada?: boolean
}) {
  const currentUserId = useAuthStore((s) => s.user?.id)
  const compact = usePreferencesStore((s) => s.ui.compactMessages)
  const member = useMember(message.guildId, message.authorId)
  const composer = useContext(MessageComposerContext)

  const [menu, setMenu] = useState<{ x: number; y: number } | null>(null)
  const [editing, setEditing] = useState(false)
  const [editValue, setEditValue] = useState(message.content)
  const [showReactionPicker, setShowReactionPicker] = useState(false)

  const isAuthor = message.authorId === currentUserId
  const displayName = member?.nickname ?? message.author.displayName

  // A cor do nome vem do cargo mais alto que define cor, como no Discord.
  const roleColor = useAppStore((s) => {
    if (!message.guildId || !member) return null
    const roles = member.roleIds
      .map((id) => s.roles[id])
      .filter((r): r is NonNullable<typeof r> => Boolean(r?.color))
      .sort((a, b) => b.position - a.position)
    return roles[0]?.color ?? null
  })

  const [bandejaAberta, setBandejaAberta] = useState(false)

  const handleReact = async (emoji: string) => {
    const existing = message.reactions.find((r) => r.emoji === emoji)
    try {
      if (existing?.me) {
        await api.messages.unreact(message.channelId, message.id, emoji)
      } else {
        await api.messages.react(message.channelId, message.id, emoji)
      }
    } catch {
      // O evento do gateway é a fonte de verdade; falha aqui não quebra a tela.
    }
    setShowReactionPicker(false)
  }

  const handleSaveEdit = async () => {
    const trimmed = editValue.trim()
    if (trimmed.length === 0 || trimmed === message.content) {
      setEditing(false)
      return
    }
    try {
      await api.messages.edit(message.channelId, message.id, trimmed)
    } finally {
      setEditing(false)
    }
  }

  return (
    <div
      // Marca de endereço: é assim que a lista de fixadas e as respostas
      // encontram esta mensagem na tela para levar a pessoa até ela.
      data-message-id={message.id}
      className={cn(
        'group relative flex gap-3 px-2 transition-colors hover:bg-surface-sunken/40',
        // Vem como classe e não como manipulação direta do elemento: o React
        // reescreve o className a cada render e apagaria o realce no caminho.
        destacada && 'mensagem-alcancada',
        grouped ? 'py-0.5' : compact ? 'pt-1 pb-0.5' : 'pt-3 pb-0.5',
        message.pending && 'opacity-60',
        // Fixar gravava no banco e não mudava nada na tela, o que fazia a ação
        // parecer quebrada. A faixa lateral e o rótulo dão o retorno visual.
        message.pinned && 'bg-warning-subtle/40 shadow-[inset_2px_0_0_0_var(--color-warning)]',
      )}
      onContextMenu={(e) => {
        e.preventDefault()
        setMenu({ x: e.clientX, y: e.clientY })
      }}
    >
      {/* Coluna do avatar. Quando agrupada, cede o espaço para o horário. */}
      <div className="w-10 shrink-0">
        {grouped ? (
          <span className="mt-0.5 hidden text-[10px] leading-5 text-content-muted group-hover:block">
            {formatTime(message.createdAt)}
          </span>
        ) : (
          <Avatar
            userId={message.authorId}
            name={displayName}
            src={message.author.avatarUrl}
            size="md"
          />
        )}
      </div>

      <div className="min-w-0 flex-1">
        {message.replyTo ? (
          <div className="mb-1 flex items-center gap-1.5 pl-2 text-[13px] text-content-muted reply-bar">
            <Avatar
              userId={message.replyTo.authorId}
              name={message.replyTo.author.displayName}
              src={message.replyTo.author.avatarUrl}
              size="xs"
            />
            <span className="font-medium text-content-secondary">
              {message.replyTo.author.displayName}
            </span>
            <span className="truncate">{message.replyTo.content}</span>
          </div>
        ) : null}

        {message.pinned ? (
          <div className="mb-1 flex items-center gap-1.5 text-[11px] font-medium text-warning">
            <Pin className="size-3" />
            Mensagem fixada
          </div>
        ) : null}

        {!grouped ? (
          <div className="flex items-baseline gap-2">
            <span
              className="text-[15px] font-semibold text-content-primary"
              style={roleColor ? { color: roleColor } : undefined}
            >
              {displayName}
            </span>
            {message.author.bot ? (
              <span className="rounded bg-brand px-1 py-px text-[10px] font-bold text-white">
                BOT
              </span>
            ) : null}
            <span className="text-[11px] text-content-muted">
              {formatTime(message.createdAt)}
            </span>
          </div>
        ) : null}

        {editing ? (
          <div className="mt-1">
            <textarea
              autoFocus
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault()
                  void handleSaveEdit()
                }
                if (e.key === 'Escape') setEditing(false)
              }}
              rows={Math.min(8, editValue.split('\n').length)}
              className="w-full resize-none rounded-lg bg-surface-deepest px-3 py-2 text-[15px] outline-none"
            />
            <p className="mt-1 text-[11px] text-content-muted">
              Enter para salvar, Esc para cancelar
            </p>
          </div>
        ) : (
          <>
            <MessageContent content={message.content} guildId={message.guildId} />
            {message.editedAt ? (
              <span className="ml-1 text-[10px] text-content-muted">(editada)</span>
            ) : null}
          </>
        )}

        {message.attachments.length > 0 ? (
          <div className="mt-2 flex flex-wrap gap-2">
            {message.attachments.map((attachment) => (
              <AttachmentPreview key={attachment.id} attachment={attachment} />
            ))}
          </div>
        ) : null}

        {message.reactions.length > 0 ? (
          <div className="mt-1.5 flex flex-wrap gap-1">
            {message.reactions.map((reaction) => (
              <button
                key={reaction.emoji}
                type="button"
                onClick={() => void handleReact(reaction.emoji)}
                className={cn(
                  'flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-xs transition-colors',
                  reaction.me
                    ? 'border-brand bg-brand-subtle text-content-primary'
                    : 'border-transparent bg-surface-overlay text-content-secondary hover:border-line-strong',
                )}
              >
                <span className="text-sm leading-none">{reaction.emoji}</span>
                <span className="font-medium tabular-nums">{reaction.count}</span>
              </button>
            ))}
          </div>
        ) : null}
      </div>

      {/* Ações rápidas, reveladas no hover. */}
      <div className="revelar-no-hover absolute -top-3 right-4 flex items-center gap-0.5 rounded-lg bg-surface-overlay p-0.5 shadow-lg">
        <div className="relative">
          <Tooltip content="Reagir">
            <button
              type="button"
              onClick={() => setShowReactionPicker((v) => !v)}
              aria-label="Reagir"
              className="flex size-7 items-center justify-center rounded text-content-secondary transition-colors hover:bg-surface-hover hover:text-content-primary"
            >
              <SmilePlus className="size-4" />
            </button>
          </Tooltip>

          {showReactionPicker ? (
            <>
              {/*
                Os atalhos continuam na frente porque a maioria das reações é um
                desses cinco, e obrigar a abrir a bandeja inteira para dar um
                joinha seria um passo a mais sem ganho nenhum.
              */}
              <div className="absolute right-0 bottom-full mb-1 flex gap-0.5 rounded-lg bg-surface-overlay p-1 shadow-xl animate-fade-in">
                {QUICK_REACTIONS.map((emoji) => (
                  <button
                    key={emoji}
                    type="button"
                    onClick={() => void handleReact(emoji)}
                    className="flex size-8 items-center justify-center rounded text-lg transition-colors hover:bg-surface-hover"
                  >
                    {emoji}
                  </button>
                ))}

                <div className="mx-0.5 w-px bg-line" />

                <button
                  type="button"
                  onClick={() => {
                    setShowReactionPicker(false)
                    setBandejaAberta(true)
                  }}
                  aria-label="Mais emojis"
                  title="Mais emojis"
                  className="flex size-8 items-center justify-center rounded text-content-secondary transition-colors hover:bg-surface-hover hover:text-content-primary"
                >
                  <SmilePlus className="size-4" />
                </button>
              </div>
            </>
          ) : null}

          {bandejaAberta ? (
            <EmojiPicker
              alinhamento="direita"
              onEscolher={(emoji) => {
                setBandejaAberta(false)
                void handleReact(emoji)
              }}
              onFechar={() => setBandejaAberta(false)}
            />
          ) : null}
        </div>

        <Tooltip content="Responder">
          <button
            type="button"
            onClick={() => composer?.setReplyTo(message)}
            aria-label="Responder"
            className="flex size-7 items-center justify-center rounded text-content-secondary transition-colors hover:bg-surface-hover hover:text-content-primary"
          >
            <CornerUpLeft className="size-4" />
          </button>
        </Tooltip>

        {isAuthor ? (
          <Tooltip content="Editar">
            <button
              type="button"
              onClick={() => {
                setEditValue(message.content)
                setEditing(true)
              }}
              aria-label="Editar"
              className="flex size-7 items-center justify-center rounded text-content-secondary transition-colors hover:bg-surface-hover hover:text-content-primary"
            >
              <Pencil className="size-4" />
            </button>
          </Tooltip>
        ) : null}

        <Tooltip content="Mais">
          <button
            type="button"
            onClick={(e) => setMenu({ x: e.clientX, y: e.clientY })}
            aria-label="Mais opções"
            className="flex size-7 items-center justify-center rounded text-content-secondary transition-colors hover:bg-surface-hover hover:text-content-primary"
          >
            <MoreHorizontal className="size-4" />
          </button>
        </Tooltip>
      </div>

      {menu ? (
        <ContextMenu x={menu.x} y={menu.y} onClose={() => setMenu(null)}>
          <MenuItem icon={<CornerUpLeft />} onClick={() => composer?.setReplyTo(message)}>
            Responder
          </MenuItem>
          <MenuItem
            icon={<Copy />}
            onClick={() => void navigator.clipboard.writeText(message.content)}
          >
            Copiar texto
          </MenuItem>
          <MenuItem
            icon={<Pin />}
            onClick={() => void api.messages.pin(message.channelId, message.id, !message.pinned)}
          >
            {message.pinned ? 'Desafixar' : 'Fixar mensagem'}
          </MenuItem>
          {isAuthor ? (
            <MenuItem
              icon={<Pencil />}
              onClick={() => {
                setEditValue(message.content)
                setEditing(true)
              }}
            >
              Editar
            </MenuItem>
          ) : null}
          <MenuSeparator />
          <MenuItem
            icon={<Trash2 />}
            danger
            onClick={() => void api.messages.remove(message.channelId, message.id)}
          >
            Excluir
          </MenuItem>
        </ContextMenu>
      ) : null}
    </div>
  )
})
